import React, { Fragment, useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import Pagination from "react-js-pagination";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faEye, faTrash, faTrashRestore, faPencilAlt } from '@fortawesome/free-solid-svg-icons'
import { faCheckSquare, faSquare } from '@fortawesome/free-regular-svg-icons'
import { Helmet } from "react-helmet-async";

import AppContext from '../../contexts/AppContext'
import Breadcrumbs from '../Breadcrumbs'
import Header from '../Header'
import Footer from '../Footer'
import { breadcrumbsBy } from '../../services/locations'
import { searchProperty, searchFromHash, setEncoded, getEncoded } from '../../services/search'

const SavedSearches = ({ match, history }) => {

    const { data, searchServices, location } = useContext(AppContext)
    const { app, isReady } = data
    const { user } = app

    const [ loading, setLoading ] = useState(false)
    const [ deleting, setDeleting ] = useState(null)
    const [ updating, setUpdating ] = useState(null)
    const [ search, setSearch ] = useState({})
    const [ searches, setSearches ] = useState({})
    const [ searchActive, setSearchActive ] = useState(false)

    const loadSearches = () => {
        return new Promise((resolve, reject) => {
            setLoading(true)
            setSearch(searchFromHash())
            searchServices.list(getEncoded()).then(res => {
                setSearches(res.searches)
                setLoading(false)
                setSearchActive(false)
                resolve()
            }).catch(res => {
                if(res.status == 404) history.push("/page-not-found")
                if(res.status == 500) history.push("/server-error")
                reject()
            })
        })
    }

    const deleteSave = (e, item) => {
        e.preventDefault()
        setDeleting(item.the_save.id)
        searchServices.deleteSearch(item.the_save.id).then(res => {
            setDeleting(null)
            setSearches({...searches, data: searches.data.filter(_item => _item.id !=item.id)})
        }).catch(res => {
            if(res.status == 404) history.push("/page-not-found")
            if(res.status == 500) history.push("/server-error")
        })
    }

    const updateOption = (e, name, value, item) => {
        e.preventDefault()
        setUpdating(item.the_save.id)
        searchServices.updateSearch(item.the_save.id, {[name]: value}).then(res => {
            setSearches({...searches, data: [...searches.data.filter(_item => _item.id !=item.id), {...item, the_save: res.userSearch} ].sort((a, b) => (a.id < b.id) ? 1 : -1)})
            setUpdating(null)
        }).catch(res => {
            if(res.status == 404) history.push("/page-not-found")
            if(res.status == 500) history.push("/server-error")
        })
    }

    useEffect(() => {
        if(isReady) loadSearches()
    }, [ history.location.hash, isReady ])

    const setSearchProperty = (type, value) => {
        const res = searchProperty(search, type, value)
        setSearch(res.search)
        if(!res.isEqual) setSearchActive(true)
        return res.search
    }

    const onSearch = newSearch => {
        setEncoded(newSearch ? newSearch : {... search})
    }

    const changePage = page => {
        setEncoded(setSearchProperty('page', page == 1 ? null : page))
    }

    const title = "Saved Searches"

    return (
        <Fragment>
            <div>
                <Header history={history} />
                <main className="py-4">
                    <div className="container">

                        <Breadcrumbs list={breadcrumbsBy(location, [{url: '/account', title: 'Dashboard'}, {title}])} />

                        <Helmet><title>{title + ' | ' + process.env.MIX_APP_NAME}</title></Helmet>

                        <div className="d-flex justify-content-between">
                            <div className="d-flex">
                                <h1>{title}</h1>
                                <div className="ms-3">
                                    <div className="btn-group btn-group-sm mt-2 me-2" role="group" aria-label="First group">
                                        <button
                                            type="button"
                                            className={'btn btn-outline-primary' + (!search.active ? ' active' : '')}
                                            onClick={() => {
                                                onSearch(setSearchProperty('active', null))
                                            }}
                                        >Active</button>
                                        <button
                                            type="button"
                                            className={'btn btn-outline-primary' + (search.active === '0' ? ' active' : '')}
                                            onClick={() => {
                                                onSearch(setSearchProperty('active', '0'))
                                            }}
                                        >Archive</button>
                                    </div>
                                </div>
                            </div>

                            {searches.data && searches.data.length > 0 && searches.last_page > 1 &&
                                <nav>
                                    <Pagination
                                        activePage={search.page}
                                        itemsCountPerPage={parseInt(searches.per_page)}
                                        totalItemsCount={searches.total}
                                        pageRangeDisplayed={5}
                                        onChange={changePage}
                                        itemClass="page-item"
                                        linkClass="page-link"
                                        prevPageText="Previous"
                                        nextPageText="Next"
                                        hideFirstLastPages={true}
                                    />
                                </nav>
                            }
                        </div>

                        {loading && <FontAwesomeIcon icon={faSpinner} />}

                        {!loading && searches.data && searches.data.length == 0 &&
                            <p className="lead">
                                There are no saved searches here yet. Use the search features of the <Link to="/" tag="a">website</Link> then click the "Save Search" button.
                            </p>
                        }

                        {!loading && searches.data && searches.data.length > 0 &&
                            <Fragment>
                                <div className="table-responsive">
                                    <table className="table table-striped table-bordered bg-white">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th className="text-center">Status</th>
                                                <th className="text-nowrap">On home page</th>
                                                <th>Notification</th>
                                                <th>Query name</th>
                                                <th>Location</th>
                                                <th>Category</th>
                                                <th>Search query</th>
                                                <th className="text-center">Manage</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searches.data.map(item => {
                                                const isActive = item.the_save.active > 0
                                                return (
                                                    <tr
                                                        key={item.the_save.id}
                                                        className={isActive ? '' : 'table-secondary'}
                                                    >
                                                        <td className="text-center">{item.id}</td>
                                                        <td className="text-center">
                                                            {isActive ? 'Active' : 'Archived'}
                                                        </td>
                                                        <td className="text-center">
                                                            <a href="" onClick={e => updateOption(e, 'on_home', !item.the_save.on_home, item)}>
                                                                {item.the_save.on_home > 0 && <FontAwesomeIcon icon={faCheckSquare} />}
                                                                {!item.the_save.on_home && <FontAwesomeIcon icon={faSquare} />}
                                                            </a>
                                                        </td>
                                                        <td className="text-center">
                                                            <a href="" onClick={e => updateOption(e, 'notification', !item.the_save.notification, item)}>
                                                                {item.the_save.notification > 0 && <FontAwesomeIcon icon={faCheckSquare} />}
                                                                {!item.the_save.notification && <FontAwesomeIcon icon={faSquare} />}
                                                            </a>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex">
                                                                <a href="" className="me-2" onClick={e => {
                                                                    e.preventDefault()
                                                                    var newName = prompt("Update saved search name", item.the_save.name)
                                                                    if(newName) updateOption(e, 'name', newName, item)
                                                                }}>
                                                                    <FontAwesomeIcon icon={faPencilAlt} />
                                                                </a>
                                                                <a href={item.url}>{item.the_save.name}</a>
                                                            </div>
                                                        </td>
                                                        <td>{item.location ? (
                                                            <a href={item.location.url}>{item.location.name}</a>
                                                        ) : '–'}</td>
                                                        <td>
                                                            {item.category ? (
                                                                <a href={item.category_url}>{item.category.name}</a>
                                                            ) : (
                                                                <span>&ndash;</span>
                                                            )}
                                                        </td>
                                                        <td>{item.query_text ? item.query_text : (<span>&ndash;</span>)}</td>
                                                        <td className="text-nowrap text-center">
                                                            <a href={item.url}>
                                                                <FontAwesomeIcon icon={faEye} />
                                                                <span className="ms-1 d-none d-xl-inline">View</span>
                                                            </a>
                                                            <a href=""className="text-secondary ms-3" onClick={e => deleteSave(e, item)}>
                                                                {isActive && <FontAwesomeIcon icon={faTrash} />}
                                                                {!isActive && <FontAwesomeIcon icon={faTrashRestore} />}
                                                                {isActive && <span className="ms-1 d-none d-xl-inline">Archive</span>}
                                                                {!isActive && <span className="ms-1 d-none d-xl-inline">Restore</span>}
                                                            </a>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {searches.data && searches.data.length > 0 && searches.last_page > 1 &&
                                    <nav>
                                        <Pagination
                                            activePage={search.page}
                                            itemsCountPerPage={parseInt(searches.per_page)}
                                            totalItemsCount={searches.total}
                                            pageRangeDisplayed={5}
                                            onChange={changePage}
                                            itemClass="page-item"
                                            linkClass="page-link"
                                            prevPageText="Previous"
                                            nextPageText="Next"
                                            hideFirstLastPages={true}
                                        />
                                    </nav>
                                }

                            </Fragment>
                        }

                    </div>
                </main>
            </div>
            <Footer />
        </Fragment>
    )
}

export default SavedSearches
